import { createContext, useContext, useEffect, useState, useMemo } from 'react'
import { ROLES } from '../config'
import { useAuth } from './AuthContext'
import { useLocation } from 'react-router-dom'
import { usersRoutes, manualsRoutes } from '../routes'

export const UserContext = createContext()

export const UserProvider = ({ children }) => {
    const { userData } = useAuth()
    const [roles, setRoles] = useState([])
    const [site, setSite] = useState()
    const [permisos, indicarPermisos] = useState([])
    const [userManual, setUserManual] = useState(null)
    const [userManuals, setUserManuals] = useState(null)
    const location = useLocation()

    // Derived state for roles
    const {
        admin,
        isOperator,
        isSapExecutive,
        isShiftManager,
        isChiefMachinery,
        isSuperAdmin,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector,
        isObserver
    } = useMemo(() => {
        let admin = false
        let isOperator = false
        let isSapExecutive = false
        let isShiftManager = false
        let isChiefMachinery = false
        let isSuperAdmin = false
        let isUniversalInspector = false
        let isUniversalChief = false
        let isOperatorInspector = false
        let isObserver = false

        if (userData && userData.role && typeof userData.role === 'object') {
            const roleName = userData.role.dbName

            if (roleName === ROLES.SUPER_ADMIN) {
                isSuperAdmin = true
                admin = true // Super admin is also admin
            }

            if (roleName === ROLES.ADMIN) {
                admin = true
            }

            if (roleName === ROLES.OBSERVER) {
                isObserver = true
            }

            if (
                roleName === ROLES.INSPECTION_WORKER ||
                roleName === ROLES.MAINTENANCE_OPERATOR ||
                roleName === ROLES.OPERATOR ||
                roleName === ROLES.OPERATOR_INSPECTOR_UNIVERSAL ||
                roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL ||
                roleName === ROLES.OPERATOR_INSPECTOR
            ) {
                isOperator = true
            }
            if (roleName === ROLES.SAP_EXECUTIVE || roleName === ROLES.OPERATOR_INSPECTOR) {
                isSapExecutive = true
            }
            if (roleName === ROLES.SHIFT_MANAGER) {
                isShiftManager = true
            }
            if (roleName === ROLES.CHIEF_MACHINERY || roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL) {
                isChiefMachinery = true
            }

            if (roleName === ROLES.OPERATOR_INSPECTOR_UNIVERSAL) {
                isUniversalInspector = true
            }
            if (roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL) {
                isUniversalChief = true
            }
            if (roleName === ROLES.OPERATOR_INSPECTOR) {
                isOperatorInspector = true
            }
        }

        // Side effects for localStorage mirroring
        localStorage.setItem('isAdmin', admin.toString())
        localStorage.setItem('isOperator', isOperator.toString())
        localStorage.setItem('isSapExecutive', isSapExecutive.toString())
        localStorage.setItem('isShiftManager', isShiftManager.toString())
        localStorage.setItem('isChiefMachinery', isChiefMachinery.toString())
        localStorage.setItem('isUniversalInspector', isUniversalInspector.toString())
        localStorage.setItem('isUniversalChief', isUniversalChief.toString())
        localStorage.setItem('isOperatorInspector', isOperatorInspector.toString())
        localStorage.setItem('isObserver', isObserver.toString())

        return {
            admin,
            isOperator,
            isSapExecutive,
            isShiftManager,
            isChiefMachinery,
            isSuperAdmin,
            isUniversalInspector,
            isUniversalChief,
            isOperatorInspector,
            isObserver
        }
    }, [userData])

    useEffect(() => {
        if (userData) {
            // Set initial roles from userData if available
            if (userData.roles) {
                setRoles(userData.roles)
            }

            // Calculate permissions cache
            if (userData.role && typeof userData.role === 'object') {
                const roleName = userData.role.dbName
                const newPermisos = []
                if (roleName === ROLES.ADMIN || roleName === ROLES.SUPER_ADMIN) newPermisos.push(ROLES.ADMIN)
                if (roleName === ROLES.OBSERVER) newPermisos.push(ROLES.OBSERVER)
                if (
                    roleName === ROLES.INSPECTION_WORKER ||
                    roleName === ROLES.MAINTENANCE_OPERATOR ||
                    roleName === ROLES.OPERATOR ||
                    roleName === ROLES.OPERATOR_INSPECTOR_UNIVERSAL ||
                    roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL ||
                    roleName === ROLES.OPERATOR_INSPECTOR
                ) newPermisos.push(ROLES.OPERATOR)
                if (roleName === ROLES.SAP_EXECUTIVE || roleName === ROLES.OPERATOR_INSPECTOR) newPermisos.push(ROLES.SAP_EXECUTIVE)
                if (roleName === ROLES.SHIFT_MANAGER) newPermisos.push(ROLES.SHIFT_MANAGER)
                if (roleName === ROLES.CHIEF_MACHINERY || roleName === ROLES.OPERATOR_MACHINERY_UNIVERSAL) newPermisos.push(ROLES.CHIEF_MACHINERY)
                indicarPermisos(newPermisos)
            }
        }
    }, [userData, location.pathname])

    const checkUserManual = async () => {
        if (userData && userData.role) {
            try {
                const roleId = typeof userData.role === 'object' ? userData.role._id : userData.role
                const res = await manualsRoutes.getAccessibleUserManual(roleId)
                const manualsList = Array.isArray(res?.data) ? res.data : (res?.data ? [res.data] : [])
                setUserManuals(manualsList)
                setUserManual(manualsList[0] || null)
            } catch (err) {
                console.error('Error fetching accessible user manuals:', err)
                setUserManuals([])
                setUserManual(null)
            }
        } else {
            setUserManuals([])
            setUserManual(null)
        }
    }

    useEffect(() => {
        checkUserManual()
    }, [userData?.role])

    const activateUser = async (email) => {
        try {
            const response = await usersRoutes.activateUser(email)
            if (response.data) {
                // Note: Updating userData is handled by AuthContext via setUserData if we passed it,
                // or we can refresh the user data. For now, we return true and let the caller handle UI.
                // ideally, we'd update the central store.
                alert('Usuario activado exitosamente.')
                return true
            }
            return false
        } catch (error) {
            console.error('Error activating user:', error)
            alert('Error al activar el usuario.')
            return false
        }
    }

    const value = {
        roles,
        site,
        setSite,
        permisos,
        admin,
        isOperator,
        isSapExecutive,
        isShiftManager,
        isChiefMachinery,
        isSuperAdmin,
        isUniversalInspector,
        isUniversalChief,
        isOperatorInspector,
        isObserver,
        activateUser,
        userManual,
        userManuals,
        checkUserManual
    }

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    )
}

export const useUser = () => useContext(UserContext)
